import {
  DataIngestionNodeData,
  IntegrationType,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Info } from 'lucide-react';
import { toast } from 'sonner';

export default function DataIngestionConfiguration({
  setNodes,
  selectedNode,
  readOnly = false,
}: {
  setNodes: any;
  selectedNode: any;
  readOnly?: boolean;
}) {
  const nodes = useNodes();
  const wF = useStore($workflow);
  const queryClient = useQueryClient();

  function getIntegrationDateRange() {
    if (wF && wF.executionFrequencyType !== null) {
      if (wF.executionFrequencyType === 'EveryDay') {
        // start date is start of today
        // end date is end of today
        return {
          startDate: 'start of each day',
          endDate: 'the end of each day',
        };
      } else if (wF.executionFrequencyType === 'Weekly') {
        return {
          startDate: 'start of each week',
          endDate: 'the end of each week',
        };
      } else if (wF.executionFrequencyType === 'Monthly') {
        return {
          startDate: 'start of each month',
          endDate: 'the end of each month',
        };
      } else if (wF.executionFrequencyType === 'Quarterly') {
        return {
          startDate: 'start of each quarter of the year',
          endDate: 'the end of each quarter of the year',
        };
      }
    } else {
      return null;
    }
  }

  function updateDataSource(dataSource: IntegrationType) {
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode = newNodes.find((n: any) => n.id === selectedNode.id);

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      // update the node data
      updatableNode.data = {
        ...updatableNode.data,
        dataSource: dataSource as IntegrationType,
        type: 'DATA_INGESTION' as WorkflowNodeType,
      };
      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;

      setNodes(newNodes);
    }
  }

  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],

      });
      toast.success('Data Source saved successfully!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });
  async function handleSave() {
    const updatableNode = nodes.find((n: any) => n.id === selectedNode.id);

    if (updatableNode) {
      try {
        await updateNode.mutateAsync({
          req: { data: updatableNode.data as DataIngestionNodeData },
          nodeId: updatableNode?.id,
        });
      } catch (error) {
        console.error('Error saving node:', error);
      }
    }
  }

  return (
    <div className="w-full h-full">
      <p className=" text-sm font-semibold [&:not(:first-child)]:mt-6">
        Data source
      </p>
      <div className="mb-4 flex items-center">
        <Info className="mr-1 h-3 w-3 text-muted-foreground" />
        <p className="tracking-light text-xs text-muted-foreground">
          Indicates the source of data to be ingested.
        </p>
      </div>
      <Select
        onValueChange={value => {
          if (!readOnly) { // Prevent changes if readOnly is true
            updateDataSource(value as IntegrationType);
          }
        }}
        value={selectedNode?.data?.dataSource}
        disabled={readOnly} // Disable dropdown in published mode
      >

        <SelectTrigger className="w-[100%]">
          <SelectValue placeholder="Select a data source" />
        </SelectTrigger>
        <SelectContent className="w-[100%]">
          <SelectGroup>
            <SelectLabel>Sources</SelectLabel>
            <SelectItem value="Zendesk">Zendesk</SelectItem>
            <SelectItem value="Aircall">Aircall</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <div className="mt-3">
        {(() => {
          const dateRange = getIntegrationDateRange();
          if (dateRange && selectedNode?.data?.dataSource) {
            return (
              <>
                <p className="w-[350px] text-xs text-muted-foreground">
                  {selectedNode?.data?.dataSource} data will be imported from{' '}
                  {dateRange.startDate} to {dateRange.endDate}
                </p>
              </>
            );
          }
          return <></>;
        })()}
      </div>

      <div className="mt-2 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={readOnly || updateNode.isPending} // ✅ Disable if readOnly
          onClick={handleSave}
        >
          Save
        </Button>

      </div>
    </div>
  );
}
